import { useState } from "react";
import {
  type Control,
  type FieldErrors,
  type UseFormClearErrors,
  type UseFormHandleSubmit,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { Row } from "@tanstack/react-table";
import {
  Input,
  Label,
  Button,
  Textarea,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  ScrollArea,
  CollapsibleTrigger,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  RadioGroup,
  RadioGroupItem,
  RichTextEditor,
  DialogContent,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  DialogHeader,
  DialogTitle,
} from "@pushpress/pushpress-ui";
import { AlertCircle } from "lucide-react";
import { cn } from "../../../../utils/css";
import { NO_REPLY_EMAIL } from "../../constants";
import {
  LeadRecordType,
  MemberRecordType,
  SendMessageFormValuesType,
  StaffRecordType,
  MESSAGE_TYPES,
  MessageType,
} from "../../types";

type SendMessageModalContentProps = {
  clearErrors: UseFormClearErrors<SendMessageFormValuesType>;
  companyEmail?: string;
  companyName: string;
  control: Control<SendMessageFormValuesType>;
  errors: FieldErrors<SendMessageFormValuesType>;
  handleFieldsReset: () => void;
  handleSendMessage: (data: SendMessageFormValuesType) => void;
  handleSubmit: UseFormHandleSubmit<SendMessageFormValuesType>;
  isLoading: boolean;
  register: UseFormRegister<SendMessageFormValuesType>;
  rowsSelected: Row<MemberRecordType | LeadRecordType | StaffRecordType>[];
  setValue: UseFormSetValue<SendMessageFormValuesType>;
  watch: UseFormWatch<SendMessageFormValuesType>;
};

export default function SendMessageModalContent({
  clearErrors,
  companyEmail,
  companyName,
  errors,
  handleFieldsReset,
  handleSendMessage,
  handleSubmit,
  isLoading,
  register,
  rowsSelected,
  setValue,
  watch,
}: SendMessageModalContentProps) {
  const VISIBLE_COUNT = 4;

  const [isListOpen, setIsListOpen] = useState(false);

  const hasCompanyEmail = companyEmail && companyEmail.length > 0;

  const showEmailForm = watch("messageType") === MESSAGE_TYPES.EMAIL;

  const visibleCount = Math.min(VISIBLE_COUNT, rowsSelected.length);
  const visiblePeople = rowsSelected.slice(0, visibleCount);
  const hiddenPeople = rowsSelected.slice(visibleCount);
  const hasMorePeople = hiddenPeople.length > 0;

  const visiblePeopleText = visiblePeople
    .map((person) => `${person.original.firstName} ${person.original.lastName}`)
    .join(", ");

  const smsTooltipText = `This SMS will come from ${companyName}, with a randomly generated no-reply phone number`;
  const emailTooltipText = hasCompanyEmail ? (
    <div>
      If you send from your Customer Service Email (configured in settings),
      &nbsp;clients will be able to respond.
      <br />
      <br />
      If you send from no-reply, this email will come from {companyName}
      &nbsp; &lt;themachine@pushpress.com&gt;
    </div>
  ) : (
    <div>
      This email will come from {companyName}
      &lt;themachine@pushpress.com&gt;
    </div>
  );

  return (
    <DialogContent
      aria-describedby={undefined}
      align="top"
      className="sm:tw-max-w-[630px]"
      onInteractOutside={(event) => event.preventDefault()}
      onEscapeKeyDown={(event) => event.preventDefault()}
      variant="noPadding"
    >
      <DialogHeader className="tw-pt-4 tw-pb-5 tw-border-b">
        <DialogTitle className="tw-text-neutral-800 tw-text-center !tw-font-medium tw-text-xl">
          Send message
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit(handleSendMessage)}>
        <div className="tw-overflow-y-auto !tw-max-h-[calc(80vh-160px)] tw-px-6">
          <div className="tw-grid tw-gap-4">
            <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-border-b tw-pb-5 tw-pt-2">
              <Label htmlFor="messageType" className="tw-text-left tw-my-auto">
                Type
              </Label>
              <div className="tw-col-span-3">
                <RadioGroup
                  value={watch("messageType")}
                  onValueChange={(value: MessageType) => {
                    setValue("messageType", value);
                    clearErrors();
                    setIsListOpen(false);
                    handleFieldsReset();
                  }}
                >
                  <div className="tw-flex tw-space-x-4">
                    <div className="tw-flex tw-items-center  tw-space-x-2">
                      <RadioGroupItem
                        data-testid="email-radio-button"
                        value={MESSAGE_TYPES.EMAIL}
                        id={MESSAGE_TYPES.EMAIL}
                        {...register("messageType")}
                      />
                      <Label htmlFor="email" className="tw-mb-0">
                        Email
                      </Label>
                    </div>
                    <div className="tw-flex tw-items-center  tw-space-x-2">
                      <RadioGroupItem
                        data-testid="sms-radio-button"
                        value={MESSAGE_TYPES.SMS}
                        id={MESSAGE_TYPES.SMS}
                        {...register("messageType")}
                      />
                      <Label htmlFor="sms" className="tw-mb-0">
                        SMS
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-border-b tw-pb-5 tw-pt-2">
              <Label htmlFor="from" className="tw-text-left tw-my-auto">
                From
              </Label>
              <div className="tw-col-span-3 tw-flex tw-items-center tw-gap-x-3 tw-flex-wrap">
                {showEmailForm && hasCompanyEmail ? (
                  <div className="tw-flex sm:tw-w-[285px] max-sm:tw-w-[200px]">
                    <Select
                      {...register("replyTo")}
                      value={watch("replyTo") || companyEmail}
                      onValueChange={(value) => {
                        setValue("replyTo", value);
                      }}
                    >
                      <SelectTrigger data-testid="email-dropdown-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={companyEmail}>
                            {companyEmail}
                          </SelectItem>
                          <SelectItem value={NO_REPLY_EMAIL}>
                            {NO_REPLY_EMAIL}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>{NO_REPLY_EMAIL}</>
                )}
                <Tooltip>
                  <TooltipTrigger>
                    <AlertCircle className="tw-h-5 tw-w-5 tw-text-info tw-pt-1" />
                  </TooltipTrigger>
                  <TooltipContent
                    className={`tw-p-4 tw-bg-slate-900 ${
                      showEmailForm ? "tw-w-[310px]" : "tw-w-[260px]"
                    }`}
                  >
                    {showEmailForm ? emailTooltipText : smsTooltipText}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-border-b tw-pb-5 tw-pt-2">
              <Label htmlFor="to" className="tw-text-left tw-my-auto">
                To
              </Label>
              <div className="tw-col-span-3">
                <Collapsible
                  open={isListOpen}
                  onOpenChange={setIsListOpen}
                  className="tw-flex-1"
                >
                  <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-x-1">
                    <p>{visiblePeopleText}</p>
                    {hasMorePeople && (
                      <>
                        <span>and</span>
                        <CollapsibleTrigger asChild>
                          <Button
                            className="tw-text-primary tw-p-0 hover:tw-bg-transparent tw-underline tw-pl-1 tw-pr-1 tw-h-[18px]"
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsListOpen(!isListOpen)}
                          >
                            {isListOpen
                              ? "show less"
                              : `${hiddenPeople.length} more`}
                          </Button>
                        </CollapsibleTrigger>
                      </>
                    )}
                  </div>
                  {hasMorePeople && (
                    <CollapsibleContent>
                      <div className="tw-pt-2">
                        <ScrollArea
                          className="tw-rounded-md tw-border tw-h-[100px]"
                          scrollHideDelay={0}
                          type="always"
                        >
                          <div className="tw-px-2">
                            {hiddenPeople.map((person) => (
                              <div
                                key={person.id}
                                className="tw-py-1 tw-text-xs tw-flex tw-flex-col"
                              >
                                <div className="tw-font-medium tw-truncate">
                                  {person.original.firstName}{" "}
                                  {person.original.lastName}
                                </div>
                                {person.original.email && (
                                  <div className="tw-break-all tw-text-neutral-500">
                                    {person.original.email}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              </div>
            </div>
            {showEmailForm && (
              <>
                <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-border-b tw-pb-5 tw-pt-2">
                  <Label htmlFor="bcc" className="tw-text-left tw-my-auto">
                    BCC
                  </Label>
                  <div className="tw-col-span-3 tw-grid sm:tw-grid-cols-[0.7fr_1.5fr_1fr] tw-grid-cols-1 tw-gap-4 sm:tw-gap-2">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Checkbox
                        {...register("bccMe")}
                        onCheckedChange={(checked: boolean) => {
                          setValue("bccMe", checked);
                        }}
                        checked={watch("bccMe")}
                        id="bcc-me"
                      />
                      <Label htmlFor="bcc-me" className="tw-mb-0">
                        Me
                      </Label>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Checkbox
                        {...register("bccCoaches")}
                        onCheckedChange={(checked: boolean) => {
                          setValue("bccCoaches", checked);
                        }}
                        checked={watch("bccCoaches")}
                        id="bcc-coaches"
                      />
                      <Label htmlFor="bcc-coaches" className="tw-mb-0">
                        Coaches / front desk
                      </Label>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Checkbox
                        {...register("bccAdmins")}
                        onCheckedChange={(checked: boolean) => {
                          setValue("bccAdmins", checked);
                        }}
                        checked={watch("bccAdmins")}
                        id="bcc-admins"
                      />
                      <Label htmlFor="bcc-admins" className="tw-mb-0">
                        Admins
                      </Label>
                    </div>
                  </div>
                </div>
                <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-border-b tw-pb-5 tw-pt-2">
                  <Label htmlFor="subject" className="tw-text-left tw-my-auto">
                    Subject
                  </Label>
                  <div className="tw-col-span-3">
                    <Input {...register("subject")} />
                  </div>
                </div>
                <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-pt-2 tw-pb-6">
                  <Label htmlFor="message" className="tw-text-left tw-my-auto">
                    Message
                  </Label>
                  <div className="tw-col-span-3 sm:tw-h-[220px] max-sm:tw-h-[250px]">
                    <RichTextEditor
                      {...register("emailText")}
                      onChange={(html: string) => {
                        setValue("emailText", html);
                        clearErrors("emailText");
                      }}
                      enableStrikethrough={false}
                      enableHighlight={false}
                      enableDivider={false}
                      enableTextColor={false}
                      height="tw-h-[160px]"
                      className={cn(
                        "!tw-text-base tw-text-black",
                        "[&_p]:tw-my-0 [&_h1]:tw-m-0 [&_h2]:tw-m-0 [&_h3]:tw-m-0",
                        "[&_ul]:tw-my-0 [&_ol]:tw-my-0 [&_li]:tw-my-0 [&_li]:marker:tw-text-black",
                        errors.emailText
                          ? "tw-border-[1px] tw-rounded-sm tw-border-error focus-visible:tw-outline-none"
                          : "",
                      )}
                    />
                    {errors.emailText && (
                      <div className="tw-h-4 tw-text-xs tw-text-error tw-mt-1">
                        {errors.emailText.message}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            {!showEmailForm && (
              <div className="tw-grid tw-grid-cols-4 tw-items-center tw-gap-4 tw-pt-2 tw-pb-6">
                <Label htmlFor="message" className="tw-text-left tw-pb-5">
                  Message
                </Label>
                <div className="tw-col-span-3 tw-h-[130px]">
                  <Textarea
                    {...register("smsMessage")}
                    className={cn(
                      "tw-h-[100px]",
                      errors.smsMessage
                        ? "tw-border-red-500 tw-focus-visible:tw-ring-red-500"
                        : "",
                    )}
                  />
                  {errors.smsMessage && (
                    <div className="tw-h-4 tw-text-xs tw-text-error tw-mt-1">
                      {errors.smsMessage.message}
                    </div>
                  )}
                  <div className="tw-h-4 tw-text-xs tw-text-neutral-600 tw-mt-1">
                    {watch("smsMessage")?.length ?? 0} / 160 characters
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="tw-flex tw-justify-end tw-border-t tw-py-6 tw-px-6">
          <Button
            type="submit"
            size="lg"
            data-testid="send-message-button"
            disabled={isLoading || rowsSelected.length === 0}
            isSubmitting={isLoading}
          >
            Send
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
